import {
  InputField,
  Label,
  Select,
  SelectItem,
  Tab,
  Tabs,
  Tooltip,
  WeekdayPicker,
} from '@energybox/react-ui-library/dist/components';
import { Delete, Trash } from '@energybox/react-ui-library/dist/icons';
import {
  CurrentUser,
  GenericErrors,
  HvacControlLabel,
  MeasurementSystem,
  MeasurementSystemToTemperatureLabel,
  TemperatureUnit,
  Locale,
  User,
  Site,
} from '@energybox/react-ui-library/dist/types';
import {
  FanMode,
  FanModeLabel,
  ThermoStatLocalSetPointDelta,
  ThermostatWorkingMode,
  ThermostatWorkingModeLabel,
} from '@energybox/react-ui-library/dist/types/HvacControl';
import {
  HvacSop,
  HvacSopTimeSlot,
} from '@energybox/react-ui-library/dist/types/Sop';
import {
  classNames,
  createTemperatureString,
  WEEKDAYS,
} from '@energybox/react-ui-library/dist/utils';
import {
  renderLocalAdjustmentTitle,
  renderLocalSetpointDeltaSign,
} from '@energybox/react-ui-library/dist/utils/thermostat';
import {
  capitalizeFirstLetterOnly,
  isDefined,
  pluralize,
} from '@energybox/react-ui-library/dist/utils/util';
import { last as getLastElement } from 'ramda';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormLabel from '@material-ui/core/FormLabel';
import Switch from '@material-ui/core/Switch';

import React, { Dispatch, useState } from 'react';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useTemperatureUnit } from '../../hooks/utils';
import { useDetermineAlreadySelectedEquipmentTypeIds } from '../../hooks/useHvacSops';
import { toggleRecommendation, updateField } from '../../actions/sops';
import SelectEquipmentType from '../../containers/Selects/SelectEquipmentType';
import { useAppLocale, useCurrentUser } from '../../hooks/useAppDetails';
import { ApplicationState } from '../../reducers';
import {
  EditableFields as SopEditableFields,
  EditSop,
  newOccupiedHvacSopTimeSlot,
} from '../../reducers/sop';
import { PropertyToLabel } from '../../types/global';
import {
  convertSecToHrsText,
  overrideTimerOptionsMap,
} from '../../utils/controls';
import { weekdayToNumberArray } from '../../utils/weekdayToNumberArray';
import EditControlsHvacFormAdjustabilityTooltip from '../EditControlsHvacForm/EditControlsHvacFormAdjustabilityTooltip';
import EditControlsHvacFormEdmTooltip from '../EditControlsHvacForm/EditControlsHvacFormEdmTooltip';
import EditControlsHvacSetPointTooltip from '../EditControlsHvacForm/EditControlsHvacSetPointTooltip';
import AddRowButton from '../EditTimeTableForm/AddRowButton';
import TimeTableTimeInput from '../EditTimeTableForm/TimeTableTimeInput';
import {
  renderSetPointInputs,
  useHvacSopsBySiteId,
} from '../HvacSopTable/HvacSopTable';
import ModalFormContent from '../ModalFormContent';
import TemperatureInputField from '../TemperatureInputField';
import ToggleRow from './ToggleRow';
import FeatureFlag from '../../containers/FeatureFlag';
import styles from './EditHvacSopForm.module.css';
import { useSetpointRecommendation } from '../../hooks/useSetpointRecommendation';
import { useGetSite } from '../../hooks/useSites';
import { SetPointRecommendation } from '../../types/analytics';
import { useGetEquipmentBySiteId } from '../../hooks/useEquipment';

interface Props {
  isNewSop: boolean;
  siteId: string;
  formStep: 'stepOne' | 'stepTwo' | 'stepThree';
  setFormStep: React.Dispatch<
    React.SetStateAction<'stepOne' | 'stepTwo' | 'stepThree'>
  >;
  equipmentAffectedCount: number;
  isOrgLevelEdit?: boolean;
}

const EditHvacSopForm: React.FC<Props> = ({
  isNewSop,
  siteId,
  formStep,
  setFormStep,
  equipmentAffectedCount,
  isOrgLevelEdit,
}) => {
  const dispatch = useDispatch();
  const [forceMinimizeCounter, setForceMinimizeCounter] = useState(0);
  const user = useCurrentUser();
  const locale = useAppLocale();
  const temperatureUnit = useTemperatureUnit();
  const sopToEdit = useHvacSopDataToEdit();
  const onFormChangeRedux = useSetHvacSop();
  const siteData = useGetSite(siteId);
  const hvacSops = useHvacSopsBySiteId(Number.parseInt(siteId));
  const alreadySelectedEquipmentTypeIds = useDetermineAlreadySelectedEquipmentTypeIds(
    hvacSops
  );
  const [
    currentlySelectedEquipmentTypeId,
    setCurrentlySelectedEquipmentTypeId,
  ] = useState(0);
  const equipmentsAtSite = useGetEquipmentBySiteId(siteId);

  const { setpointRecommendation } = useSetpointRecommendation(siteId);

  const showRecommendation = useSelector<ApplicationState, boolean>(
    ({ sops }) => sops.showRecommendation
  );

  const isVisible = useSelector<ApplicationState, boolean>(({ sops }) => {
    return sops.showNewOrEditHvacSopModal;
  });

  if (isVisible === false || !isDefined(sopToEdit)) {
    return <></>;
  }
  if (isNewSop && !isOrgLevelEdit) {
    equipmentAffectedCount = Object.values(equipmentsAtSite).filter(
      equipment => equipment.typeId === currentlySelectedEquipmentTypeId
    ).length;
  }
  const { fields: hvacSopFields, formErrors, formErrorsVisible } = sopToEdit;
  const determineFormBody = () => {
    switch (formStep) {
      case 'stepOne': {
        return renderStepOne(
          hvacSopFields,
          onFormChangeRedux,
          alreadySelectedEquipmentTypeIds,
          formErrors,
          formErrorsVisible,
          setCurrentlySelectedEquipmentTypeId,
          isOrgLevelEdit,
          siteData
        );
      }

      case 'stepTwo': {
        return renderStepTwo(
          hvacSopFields,
          setpointRecommendation,
          showRecommendation,
          onFormChangeRedux,
          user,
          locale,
          formErrors,
          formErrorsVisible,
          temperatureUnit,
          forceMinimizeCounter,
          setForceMinimizeCounter,
          dispatch
        );
      }

      case 'stepThree': {
        return renderStepThree(isNewSop, equipmentAffectedCount);
      }

      default:
        return;
    }
  };

  const formBody = determineFormBody();
  return (
    <div>
      {formStep !== 'stepThree' && (
        <Tabs className={styles.tabs}>
          <Tab
            active={formStep === 'stepOne'}
            onClick={() => {
              setFormStep('stepOne');
            }}
          >
            Step One
          </Tab>
          <Tab
            active={formStep === 'stepTwo'}
            onClick={() => {
              setFormStep('stepTwo');
            }}
          >
            Step Two
          </Tab>
        </Tabs>
      )}

      <>{formBody}</>
    </div>
  );
};

const renderStepOne = (
  hvacSopFields: SopEditableFields,
  onFormChangeRedux,
  alreadySelectedEquipmentTypeIds: number[],
  formErrors: GenericErrors,
  formErrorsVisible: boolean,
  setCurrentlySelectedEquipmentTypeId,
  isOrgLevelEdit?: boolean,
  siteData?: Site
) => {
  const { title, description, components, equipmentTypeIds } = hvacSopFields;
  const siteName = siteData?.title;
  const {
    type,
    hvacSettings: {
      enableEdm,
      enableLocalAdjustment,
      overrideTimer,
      setPointLimitDelta,
      thermostatDisplayUnits,
    },
  } = components[0] as HvacSop;

  const isEquipmentTypeAlreadySelected =
    equipmentTypeIds.length > 0
      ? alreadySelectedEquipmentTypeIds.includes(equipmentTypeIds[0])
      : false;

  return (
    <>
      <ModalFormContent className={styles.modalFormContent}>
        <div>
          <Label required>
            <b>{PropertyToLabel.title}</b>
          </Label>
        </div>
        <div>
          <InputField
            className={styles.inputWidth}
            type="text"
            name="title"
            autoComplete="title"
            value={title}
            onChange={({ currentTarget }) =>
              onFormChangeRedux(currentTarget.name, currentTarget.value)
            }
            error={formErrorsVisible && !!formErrors.title}
            customErrorText={formErrors.title && formErrors.title[0]}
          />
        </div>

        <div>
          <Label>
            <b>{PropertyToLabel.description}</b>
          </Label>
        </div>
        <div>
          <InputField
            className={styles.inputWidth}
            type="text"
            name="description"
            autoComplete="description"
            value={description}
            onChange={({ currentTarget }) =>
              onFormChangeRedux(currentTarget.name, currentTarget.value)
            }
          />
        </div>

        {isOrgLevelEdit && (
          <>
            <div>
              <Label>
                <b>{PropertyToLabel.siteId}</b>
              </Label>
            </div>
            <div>
              <InputField
                className={styles.inputWidth}
                type="text"
                name="Site"
                autoComplete="Site"
                value={siteName}
                onChange={() => {}}
                disabled={true}
              />
            </div>
          </>
        )}

        <div>
          <Label>
            <b>{PropertyToLabel.policy}</b>
          </Label>
        </div>
        <div>
          <InputField
            className={styles.inputWidth}
            type="text"
            name="policy"
            autoComplete="policy"
            value={type}
            onChange={() => {}}
            disabled={true}
          />
        </div>

        <div>
          <Label>
            <b>{PropertyToLabel.organizationUnit}</b>
          </Label>
        </div>
        <div>
          <InputField
            className={styles.inputWidth}
            type="text"
            name="attatchedTo"
            autoComplete="attatchedTo"
            value="Equipment Type"
            onChange={() => {}}
            disabled={true}
          />
        </div>

        <div>
          <Label required>
            <b>{PropertyToLabel.typeId}</b>
          </Label>
        </div>
        <div>
          <SelectEquipmentType
            listClassName={styles.selectEquipmentType}
            value={equipmentTypeIds[0]}
            isOrgLevelEdit={isOrgLevelEdit}
            onSelect={value => {
              onFormChangeRedux('equipmentTypeIds', [value]);
              setCurrentlySelectedEquipmentTypeId(value);
            }}
            fullWidth={false}
            useControlledOnly
            useHvacOnly
            error={
              isEquipmentTypeAlreadySelected ||
              (formErrorsVisible && !!formErrors.equipmentTypeIds)
            }
            customErrorText={
              isEquipmentTypeAlreadySelected
                ? 'The selected Equipment type(s) are already in use with this SOP type. Please update the existing assignment or delete it to create a new one.'
                : formErrors.equipmentTypeIds && formErrors.equipmentTypeIds[0]
            }
          />
        </div>
      </ModalFormContent>
      <div className={styles.dividerLine}></div>

      <ModalFormContent className={styles.modalFormContent}>
        <div className={styles.row}>
          <Label>{HvacControlLabel.ADVANCED_SETTINGS}</Label>
        </div>
        <div />

        <div>
          <b>
            <Label required>{HvacControlLabel.THERMOSTAT_DISPLAY}</Label>
          </b>
        </div>
        <div>
          <Select
            title={MeasurementSystemToTemperatureLabel[thermostatDisplayUnits]}
            fullWidth={false}
            error={
              formErrorsVisible &&
              !!formErrors['components.thermostatDisplayUnits']
            }
            customErrorText={
              formErrors['components.thermostatDisplayUnits'] &&
              formErrors['components.thermostatDisplayUnits'][0]
            }
          >
            {Object.values(MeasurementSystem).map(ms => (
              <SelectItem
                key={ms}
                isSelected={ms === thermostatDisplayUnits}
                onSelect={() =>
                  onFormChangeRedux(
                    'components.0.hvacSettings.thermostatDisplayUnits',
                    ms
                  )
                }
              >
                {MeasurementSystemToTemperatureLabel[ms]}
              </SelectItem>
            ))}
          </Select>
        </div>

        <div>
          <Label>
            <b>{HvacControlLabel.LOCAL_ADJUSTMENT}</b>
          </Label>
        </div>
        <div className={styles.localAdjustmentContainer}>
          <FormControlLabel
            label={
              <FormLabel
                className={styles.localAdjustmentSwitch}
                // color={enableLocalAdjustment ? "primary" : "primary"}
                classes={{ colorSecondary: styles.localAdjustmentGrey }}
              >
                {enableLocalAdjustment === true ? (
                  <span className={styles.localAdjustmentOn}>On</span>
                ) : (
                  <span className={styles.localAdjustmentOff}>Off</span>
                )}
              </FormLabel>
            }
            control={
              <Switch
                checked={enableLocalAdjustment}
                color={enableLocalAdjustment ? 'primary' : 'default'}
                onChange={() => {
                  const willBeEnabled = !enableLocalAdjustment;
                  onFormChangeRedux(
                    'components.0.hvacSettings.enableLocalAdjustment',
                    willBeEnabled
                  );
                  if (willBeEnabled === false) {
                    onFormChangeRedux(
                      'components.0.hvacSettings.setPointLimitDelta',
                      null
                    );
                    onFormChangeRedux(
                      'components.0.hvacSettings.overrideTimer',
                      null
                    );
                  } else {
                    onFormChangeRedux(
                      'components.0.hvacSettings.setPointLimitDelta',
                      2
                    );
                    onFormChangeRedux(
                      'components.0.hvacSettings.overrideTimer',
                      overrideTimerOptionsMap.ONE_HOUR
                    );
                  }
                }}
                name="localAdjustment"
              />
            }
          />

          <div className={styles.localAdjustmentTooltip}>
            <EditControlsHvacFormAdjustabilityTooltip arrowDirection="right" />
          </div>
        </div>

        <div
          className={
            enableLocalAdjustment === undefined ||
            enableLocalAdjustment === false
              ? styles.remove
              : ''
          }
        >
          <b>
            <Label>{HvacControlLabel.SETPOINT_LIMITS}</Label>
          </b>
        </div>
        {/* pass ThermostatWorkingMode.AUTO as working mode below to render the sign as +/- since we 
        don't know more specifically here. each time slot in the SOP can be auto, cool, or heat */}
        <div
          className={
            enableLocalAdjustment === undefined ||
            enableLocalAdjustment === false
              ? styles.remove
              : ''
          }
        >
          <Select
            title={renderLocalAdjustmentTitle(
              ThermostatWorkingMode.AUTO,
              setPointLimitDelta
            )}
            disabled={
              enableLocalAdjustment === undefined ||
              enableLocalAdjustment === false
            }
            fullWidth={false}
            // error={
            //   formErrorsVisible &&
            //   enableLocalAdjustment === true &&
            //   !!formErrors.setPointLimitDelta
            // }
            // customErrorText={formErrors.setPointLimitDelta}
          >
            {Object.values(ThermoStatLocalSetPointDelta)
              .filter(deltaOption => Number.isSafeInteger(deltaOption))
              .map(deltaOption => {
                return (
                  <SelectItem
                    key={deltaOption}
                    isSelected={deltaOption === setPointLimitDelta}
                    onSelect={() =>
                      onFormChangeRedux(
                        'components.0.hvacSettings.setPointLimitDelta',
                        deltaOption
                      )
                    }
                  >
                    {renderLocalSetpointDeltaSign(ThermostatWorkingMode.AUTO)}
                    {deltaOption}
                    {` ${pluralize('degree', deltaOption as number)}`}
                  </SelectItem>
                );
              })}
          </Select>
        </div>

        <div
          className={
            enableLocalAdjustment === undefined ||
            enableLocalAdjustment === false
              ? styles.remove
              : ''
          }
        >
          <b>
            <Label>{HvacControlLabel.REVERT_ADJUSTMENT}</Label>
          </b>
        </div>
        <div
          className={
            (styles.inlineDescriptionHeader,
            enableLocalAdjustment === undefined ||
            enableLocalAdjustment === false
              ? styles.remove
              : '')
          }
        >
          <Select
            title={convertSecToHrsText(overrideTimer)}
            disabled={
              enableLocalAdjustment === undefined ||
              enableLocalAdjustment === false
            }
            fullWidth={false}
          >
            {Object.values(overrideTimerOptionsMap).map(overrideTimerInSec => {
              return (
                <SelectItem
                  key={overrideTimerInSec}
                  isSelected={overrideTimerInSec === overrideTimer}
                  onSelect={() =>
                    onFormChangeRedux(
                      'components.0.hvacSettings.overrideTimer',
                      overrideTimerInSec
                    )
                  }
                >
                  {convertSecToHrsText(overrideTimerInSec)}
                </SelectItem>
              );
            })}
          </Select>
        </div>

        <FeatureFlag>
          <>
            <div>
              <Label>
                <b>{HvacControlLabel.ENABLE_EDM}</b>
              </Label>
            </div>
            <div className={styles.localAdjustmentContainer}>
              <FormControlLabel
                label={
                  <FormLabel
                    className={styles.localAdjustmentSwitch}
                    // color={enableEdm ? 'primary' : 'primary'}
                    color="primary"
                    classes={{ colorSecondary: styles.localAdjustmentGrey }}
                  >
                    {enableEdm === true ? (
                      <span className={styles.localAdjustmentOn}>On</span>
                    ) : (
                      <span className={styles.localAdjustmentOff}>Off</span>
                    )}
                  </FormLabel>
                }
                control={
                  <Switch
                    checked={enableEdm}
                    color={enableEdm ? 'primary' : 'default'}
                    onChange={() => {
                      onFormChangeRedux(
                        'components.0.hvacSettings.enableEdm',
                        !enableEdm
                      );
                    }}
                    name="enableEdm"
                  />
                }
              />

              <div className={styles.localAdjustmentTooltip}>
                <EditControlsHvacFormEdmTooltip arrowDirection="right" />
              </div>
            </div>
          </>
        </FeatureFlag>
      </ModalFormContent>
    </>
  );
};

const renderStepTwo = (
  hvacSopFields: SopEditableFields,
  setpointRecommendation: SetPointRecommendation[],
  showRecommendation,
  onFormChangeRedux,
  user: CurrentUser | undefined,
  locale: Locale,
  formErrors: GenericErrors,
  formErrorsVisible: boolean,
  temperatureUnit: TemperatureUnit,
  forceMinimizeCounter: number,
  setForceMinimizeCounter: React.Dispatch<React.SetStateAction<number>>,
  dispatch: Dispatch<any>
) => {
  const { components } = hvacSopFields;
  const {
    hvacSettings: { hvacSchedules },
  } = components[0] as HvacSop;

  const insertTimeSlot = () => {
    const unoccupiedTimeSlot = getLastElement(hvacSchedules);
    const existingOccupiedTimeSlots = hvacSchedules.slice(0, -1);
    const newTimeSlots = [
      ...existingOccupiedTimeSlots,
      newOccupiedHvacSopTimeSlot(),
      unoccupiedTimeSlot,
    ];

    onFormChangeRedux('components.0.hvacSettings.hvacSchedules', newTimeSlots);
    setForceMinimizeCounter(forceMinimizeCounter + 1);
  };

  return (
    <>
      <div>
        {renderUnoccupied(
          temperatureUnit,
          hvacSopFields,
          user,
          locale,
          onFormChangeRedux,
          formErrors,
          formErrorsVisible,
          forceMinimizeCounter
        )}
        {renderOccupied(
          temperatureUnit,
          hvacSopFields,
          setpointRecommendation,
          showRecommendation,
          user,
          locale,
          onFormChangeRedux,
          formErrors,
          formErrorsVisible,
          forceMinimizeCounter,
          dispatch
        )}
        <AddRowButton onClick={() => insertTimeSlot()}>
          Add Time Slot
        </AddRowButton>
      </div>
    </>
  );
};

const renderUnoccupied = (
  temperatureUnit: TemperatureUnit,
  hvacSopFields: SopEditableFields,
  user: CurrentUser | undefined,
  locale: Locale,
  onFormChangeRedux,
  formErrors: GenericErrors,
  formErrorsVisible: boolean,
  forceMinimizeCounter: number
) => {
  const { components } = hvacSopFields;
  const {
    hvacSettings: { hvacSchedules },
  } = components[0] as HvacSop;

  //HvacSchedules are stored in redux in such a way that
  //unoccupied slot is always the last element
  const unoccupiedSlot = getLastElement(hvacSchedules);
  const unoccupiedSlotIndex = hvacSchedules.length - 1;

  if (!isDefined(unoccupiedSlot)) return null;

  const { thermostatWorkingMode, fanMode } = unoccupiedSlot;

  const minTemp = Number.isNaN(unoccupiedSlot.minTemp)
    ? 0
    : unoccupiedSlot.minTemp;
  const maxTemp = Number.isNaN(unoccupiedSlot.maxTemp)
    ? 0
    : unoccupiedSlot.maxTemp;

  const isThermostatWorkingModeOff =
    thermostatWorkingMode === ThermostatWorkingMode.OFF;

  const hasMinOrMaxTempError =
    !!formErrors[`components.${unoccupiedSlotIndex}.maxTemp`] ||
    !!formErrors[`components.${unoccupiedSlotIndex}.minTemp`];

  const minimizedContent = (
    <>
      <div className={classNames(styles.row, styles.rowEnd)}>
        Mode: {thermostatWorkingMode}
      </div>
      <div
        className={classNames(
          hasMinOrMaxTempError ? styles.redText : '',
          styles.row,
          styles.rowEnd
        )}
      >
        {renderSetPointInputs(
          thermostatWorkingMode,
          minTemp,
          maxTemp,
          user,
          locale
        )}
      </div>
    </>
  );

  const onChange = (name: string, value: any) => {
    let updatedHvacSchedules = [...hvacSchedules];

    updatedHvacSchedules[updatedHvacSchedules.length - 1] = {
      ...updatedHvacSchedules[updatedHvacSchedules.length - 1],
      [name]: value,
    };

    onFormChangeRedux(
      'components.0.hvacSettings.hvacSchedules',
      updatedHvacSchedules
    );
  };

  const infoIcon = (
    <Tooltip
      underline={false}
      disableAutoAdjust
      arrowDirection="left"
      extraClassNames={[styles.tooltip]}
      content={
        <div className={styles.tooltipText}>
          <div className={styles.tooltipLineEntry}>
            This time slot is auto-generated from all occupied time-slots.
          </div>
        </div>
      }
    >
      <IoIosInformationCircleOutline color="black" size={20} />
    </Tooltip>
  );
  return (
    <ToggleRow
      forceMinimizeCounter={forceMinimizeCounter}
      title={'Unoccupied Hours *'}
      minimizedContent={minimizedContent}
      titleRowAction={infoIcon}
      className={styles.backgroundShade}
    >
      <ModalFormContent className={styles.modalFormContent}>
        <div className={styles.labelText}>
          <Label required>{HvacControlLabel.THERMOSTAT_MODE}</Label>
        </div>
        <div>
          <Select
            title={ThermostatWorkingModeLabel[thermostatWorkingMode]}
            fullWidth={false}
          >
            {Object.values(ThermostatWorkingMode).map(mode => (
              <SelectItem
                key={mode}
                isSelected={mode === thermostatWorkingMode}
                onSelect={() => onChange('thermostatWorkingMode', mode)}
              >
                {ThermostatWorkingModeLabel[mode]}
              </SelectItem>
            ))}
          </Select>
        </div>

        {!isThermostatWorkingModeOff && (
          <>
            <div className={styles.labelText}>
              <Label required>Thermostat Range</Label>
            </div>
            <div className={styles.inputFieldContainer}>
              <div className={styles.row}>
                {renderSetPointRangeInputFields(
                  minTemp,
                  maxTemp,
                  thermostatWorkingMode,
                  onChange,
                  unoccupiedSlotIndex,
                  formErrors,
                  formErrorsVisible
                )}
                <EditControlsHvacSetPointTooltip
                  temperatureUnit={temperatureUnit}
                  arrowDirection="left"
                  hasError={
                    formErrorsVisible &&
                    (!!formErrors[
                      `components.${unoccupiedSlotIndex}.maxTemp`
                    ] ||
                      !!formErrors[`components.${unoccupiedSlotIndex}.minTemp`])
                  }
                />
              </div>
            </div>
          </>
        )}

        {!isThermostatWorkingModeOff && (
          <>
            <div className={styles.labelText}>
              <Label required>{HvacControlLabel.FAN_MODE}</Label>
            </div>
            <div>
              <Select title={FanModeLabel[fanMode]} fullWidth={false}>
                {Object.values(FanMode).map(mode => (
                  <SelectItem
                    key={mode}
                    isSelected={mode === fanMode}
                    onSelect={() => onChange('fanMode', mode)}
                  >
                    {FanModeLabel[mode]}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </>
        )}
      </ModalFormContent>
    </ToggleRow>
  );
};

const renderOccupied = (
  temperatureUnit: TemperatureUnit,
  hvacSopFields: SopEditableFields,
  setpointRecommendation: SetPointRecommendation[],
  showRecommendation: boolean,
  user: CurrentUser | undefined,
  locale: Locale,
  onFormChangeRedux,
  formErrors: GenericErrors,
  formErrorsVisible: boolean,
  forceMinimizeCounter: number,
  dispatch: Dispatch<any>
) => {
  const { components } = hvacSopFields;
  const {
    hvacSettings: { hvacSchedules },
  } = components[0] as HvacSop;

  const equipment = setpointRecommendation[0]?.equipment;
  const matchedSopRecommendation = equipment?.find(
    item => item.sop?.id === hvacSopFields.id
  );
  const matchedComponents = matchedSopRecommendation?.sop?.components;
  const recommendation =
    matchedComponents && matchedComponents[0].hvacSettings.hvacSchedules[0];

  const unoccupiedSlot = getLastElement(hvacSchedules);
  const occupiedSlots = hvacSchedules.slice(0, -1);

  return occupiedSlots.map((hvacTimeSlot, index) => {
    const { timetable, fanMode, thermostatWorkingMode } = hvacTimeSlot;

    const minTemp = Number.isNaN(hvacTimeSlot.minTemp)
      ? 0
      : hvacTimeSlot.minTemp;
    const maxTemp = Number.isNaN(hvacTimeSlot.maxTemp)
      ? 0
      : hvacTimeSlot.maxTemp;

    if (!isDefined(timetable) || !isDefined(timetable.rows)) {
      return null;
    }

    const isThermostatWorkingModeOff =
      thermostatWorkingMode === ThermostatWorkingMode.OFF;

    const { rows } = timetable;
    const rowNumber = index + 1;

    const hasWeekdaysPickerError =
      formErrorsVisible && !!formErrors[`components.${index}.weekdays`];
    const hasTimeSlotError =
      formErrorsVisible &&
      (!!formErrors[`components.${index}.beginTime`] ||
        !!formErrors[`components.${index}.endTime`]);
    const hasMinOrMaxTempError =
      formErrorsVisible &&
      (!!formErrors[`components.${index}.maxTemp`] ||
        !!formErrors[`components.${index}.minTemp`]);

    const onChange = (name, value) => {
      const editedOccupiedSlots = occupiedSlots;

      editedOccupiedSlots[index] = {
        ...hvacTimeSlot,
        [name]: value,
      };

      let newTimeSlots = [...editedOccupiedSlots, unoccupiedSlot];

      onFormChangeRedux(
        'components.0.hvacSettings.hvacSchedules',
        newTimeSlots
      );
    };

    const onChangeTime = (name, value) => {
      // try to clean up this func..or at least try to give some better names & comments
      const editedOccupiedSlots = [...occupiedSlots];

      const entryRows = editedOccupiedSlots[index].timetable.rows;
      const entryKey = name === 'beginTime' ? 'begin' : 'end';
      if (!isDefined(entryRows) || !isDefined(entryRows[0])) return;
      const timetableRow = entryRows[0];

      const { time, ...rest } = timetableRow[`${entryKey}`];
      const editedRow = {
        ...timetableRow,
        [entryKey]: {
          ...rest,
          time: value,
        },
      };

      editedOccupiedSlots[index] = {
        ...hvacTimeSlot,
        timetable: {
          ...hvacTimeSlot.timetable,
          rows: [{ ...editedRow }],
        },
      };

      let newTimeSlots = [...editedOccupiedSlots, unoccupiedSlot];

      onFormChangeRedux(
        'components.0.hvacSettings.hvacSchedules',
        newTimeSlots
      );
    };

    const minimizedContent = (
      <>
        <div
          key={`minimized-occupied-${rowNumber}`}
          className={classNames(styles.row, styles.rowSpaceBetween)}
        >
          {rows[0].weekdays.length > 0 ? (
            <span className={hasWeekdaysPickerError ? styles.redText : ''}>
              {rows[0].weekdays
                .map(day => capitalizeFirstLetterOnly(day))
                .join(', ')}
            </span>
          ) : (
            <span className={styles.minContentMissingLabel}>
              Need to select days
            </span>
          )}
          <span>Mode: {thermostatWorkingMode}</span>
        </div>
        <div className={classNames(styles.row, styles.rowSpaceBetween)}>
          {!!rows[0].begin.time && !!rows[0].end.time ? (
            <span className={hasTimeSlotError ? styles.redText : ''}>
              {`${rows[0].begin.time.slice(0, 5)} - ${rows[0].end.time.slice(
                0,
                5
              )}`}
            </span>
          ) : (
            <span className={styles.minContentMissingLabel}>
              Need to select times
            </span>
          )}

          <span className={hasMinOrMaxTempError ? styles.redText : ''}>
            {renderSetPointInputs(
              thermostatWorkingMode,
              minTemp,
              maxTemp,
              user,
              locale
            )}
          </span>
        </div>
      </>
    );

    const infoIcon = (
      <Tooltip
        underline={false}
        disableAutoAdjust
        arrowDirection="left"
        extraClassNames={[styles.tooltip]}
        content={
          <div className={styles.tooltipText}>
            <div className={styles.tooltipLineEntry}>
              Add occupied time slots for different set point requirements
              throughout the week.
            </div>
            <div className={styles.tooltipLineEntry}>
              Control Mode - Force on - will use Occupied_1 set points.
            </div>
          </div>
        }
      >
        <IoIosInformationCircleOutline color="black" size={20} />
      </Tooltip>
    );

    return (
      <>
        <ToggleRow
          key={`occupied-${rowNumber}`}
          forceMinimizeCounter={forceMinimizeCounter}
          title={
            index === 0
              ? `Occupied Hour_${rowNumber} *`
              : `Occupied Hour_${rowNumber}`
          }
          minimizedContent={minimizedContent}
          titleRowAction={
            index > 0 ? (
              <Trash
                size={16}
                className={styles.trashButton}
                onClick={() => {
                  const editedOccupiedSlots = [...occupiedSlots];
                  editedOccupiedSlots.splice(index, 1);
                  let newTimeSlots = [...editedOccupiedSlots, unoccupiedSlot];
                  onFormChangeRedux(
                    'components.0.hvacSettings.hvacSchedules',
                    newTimeSlots
                  );
                }}
              />
            ) : (
              infoIcon
            )
          }
        >
          <ModalFormContent className={styles.modalFormContent}>
            <div className={styles.labelText}>
              <Label htmlFor={`${index}dayPicker`} required>
                Days
              </Label>
            </div>
            <div>
              <WeekdayPicker
                onChange={onWeekdaySelect(
                  hvacTimeSlot,
                  index,
                  unoccupiedSlot,
                  occupiedSlots,
                  onFormChangeRedux
                )}
                id={index}
                selectedWeekdays={weekdayToNumberArray(rows[0].weekdays)}
                error={hasWeekdaysPickerError}
              />
            </div>

            <div className={styles.labelText}>
              <Label htmlFor={`${index}startTime`} required>
                Start time
              </Label>
            </div>
            <TimeTableTimeInput
              className={rows[0].overrideType !== 'AUTO' ? styles.hidden : ''}
              id="beginTime"
              name={`${index}startTime`}
              value={isDefined(rows[0].begin.time) ? rows[0].begin.time : ''}
              type={rows[0].begin.type}
              onChange={onChangeTime}
              error={
                formErrorsVisible &&
                !!formErrors[`components.${index}.beginTime`]
              }
              customErrorText={
                formErrors[`components.${index}.beginTime`]?.[0] ||
                'Invalid start time'
              }
            />

            <div className={styles.labelText}>
              <Label htmlFor={`${index}endTime`} required>
                End Time
              </Label>
            </div>
            <TimeTableTimeInput
              id="endTime"
              name={`${index}endTime`}
              value={isDefined(rows[0].end.time) ? rows[0].end.time : ''}
              type={rows[0].end.type}
              onChange={onChangeTime}
              error={
                formErrorsVisible && !!formErrors[`components.${index}.endTime`]
              }
              customErrorText={
                formErrors[`components.${index}.endTime`]?.[0] ||
                'Invalid end time'
              }
            />

            <div className={styles.labelText}>
              <Label required>{HvacControlLabel.THERMOSTAT_MODE}</Label>
            </div>
            <div>
              <Select
                title={ThermostatWorkingModeLabel[thermostatWorkingMode]}
                fullWidth={false}
              >
                {Object.values(ThermostatWorkingMode).map(mode => (
                  <SelectItem
                    key={mode}
                    isSelected={mode === thermostatWorkingMode}
                    onSelect={() => onChange('thermostatWorkingMode', mode)}
                  >
                    {ThermostatWorkingModeLabel[mode]}
                  </SelectItem>
                ))}
              </Select>
            </div>

            {!isThermostatWorkingModeOff && (
              <>
                <div className={styles.labelText}>
                  <Label required>Thermostat Range</Label>
                </div>
                <div className={styles.inputFieldContainer}>
                  <div className={styles.row}>
                    {renderSetPointRangeInputFields(
                      minTemp,
                      maxTemp,
                      thermostatWorkingMode,
                      onChange,
                      index,
                      formErrors,
                      formErrorsVisible
                    )}
                    <EditControlsHvacSetPointTooltip
                      temperatureUnit={temperatureUnit}
                      arrowDirection="left"
                      hasError={
                        formErrorsVisible &&
                        (!!formErrors[`components.${index}.maxTemp`] ||
                          !!formErrors[`components.${index}.minTemp`])
                      }
                    />
                  </div>
                </div>

                {recommendation && showRecommendation && (
                  <FeatureFlag>
                    <div className={styles.setPointRecommendation}>
                      <div
                        className={styles.dismissRecommendation}
                        onClick={() => dispatch(toggleRecommendation(false))}
                      >
                        <Delete size={18} />
                      </div>
                      {' Recommendation: '}
                      <div className={styles.changeSetpoint}>
                        <span>{'Change Setpoints: '}</span>
                        <br />
                        <span>
                          {recommendation.minTemp !== null && (
                            <>
                              {`Heat to `}
                              <div className={styles.recommendationTemp}>
                                {`${createTemperatureString(
                                  recommendation['minTemp'],
                                  user || ({ temperature: 'CELSIUS' } as User),
                                  0
                                )}`}
                              </div>
                            </>
                          )}
                        </span>
                        <span>
                          {recommendation.maxTemp !== null && (
                            <>
                              {`Cool to `}
                              <div className={styles.recommendationTemp}>
                                {`${createTemperatureString(
                                  recommendation['maxTemp'],
                                  user || ({ temperature: 'CELSIUS' } as User),
                                  0
                                )}`}
                              </div>
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </FeatureFlag>
                )}
              </>
            )}

            {!isThermostatWorkingModeOff && (
              <>
                <div className={styles.labelText}>
                  <Label required>{HvacControlLabel.FAN_MODE}</Label>
                </div>
                <div>
                  <Select title={FanModeLabel[fanMode]} fullWidth={false}>
                    {Object.values(FanMode).map(mode => (
                      <SelectItem
                        key={mode}
                        isSelected={mode === fanMode}
                        onSelect={() => onChange('fanMode', mode)}
                      >
                        {FanModeLabel[mode]}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </>
            )}
          </ModalFormContent>
        </ToggleRow>
        <div className={styles.dividerLine}></div>
      </>
    );
  });
};

const renderSetPointRangeInputFields = (
  minTemp: number | undefined,
  maxTemp: number | undefined,
  thermostatWorkingMode: ThermostatWorkingMode,
  onChange: (name: any, value: any) => void,
  formErrorsIndex: number,
  formErrors: GenericErrors,
  formErrorsVisible: boolean
) => {
  const renderLabelAndInput = (
    label: HvacControlLabel.MAX_TEMP | HvacControlLabel.MIN_TEMP,
    value: number | undefined
  ) => {
    const fieldName =
      label === HvacControlLabel.MAX_TEMP ? 'maxTemp' : 'minTemp';
    const formKey = `components.${formErrorsIndex}.${fieldName}`;

    return (
      <div className={styles.inputField}>
        <span className={styles.setPointLabel}>{label}</span>
        <TemperatureInputField
          integersOnly
          className={styles.input}
          id={fieldName}
          name={fieldName}
          value={value}
          onChange={v => onChange(fieldName, v)}
          error={formErrorsVisible && !!formErrors[formKey]}
          customErrorText={'Invalid'}
        />
      </div>
    );
  };

  switch (thermostatWorkingMode) {
    case ThermostatWorkingMode.HEATING:
      return <>{renderLabelAndInput(HvacControlLabel.MIN_TEMP, minTemp)}</>;

    case ThermostatWorkingMode.COOLING:
      return <>{renderLabelAndInput(HvacControlLabel.MAX_TEMP, maxTemp)}</>;

    case ThermostatWorkingMode.AUTO:
      return (
        <div className={styles.row}>
          {renderLabelAndInput(HvacControlLabel.MIN_TEMP, minTemp)}
          <div className={styles.inputSeparator}> : </div>
          {renderLabelAndInput(HvacControlLabel.MAX_TEMP, maxTemp)}
        </div>
      );

    default:
      return null;
  }
};

const renderStepThree = (isNewSop: boolean, equipmentAffectedCount: number) => {
  if (isNewSop) {
    return (
      <div>
        <div className={styles.marginBottom}>
          This SOP update will be implemented to{' '}
          <span className={styles.boldText}>
            {equipmentAffectedCount} equipment.
          </span>
        </div>
        <div className={styles.marginBottom}>
          With this SOP you are creating your HVAC controls settings.
        </div>
        <div>
          <i>
            {' '}
            Please note: Mapped Thermostat is required per equipment to enable
            controls functionality.{' '}
          </i>
        </div>
      </div>
    );
  } else
    return (
      <div>
        <div className={styles.marginBottom}>
          This SOP update will be implemented to{' '}
          <span className={styles.boldText}>
            {equipmentAffectedCount} equipment.
          </span>
        </div>

        <div>With this SOP you are updating the HVAC controls settings.</div>
      </div>
    );
};

const onWeekdaySelect = (
  hvacTimeSlot: HvacSopTimeSlot,
  index: number,
  unoccupiedSlot: HvacSopTimeSlot,
  occupiedSlots: HvacSopTimeSlot[],
  onFormChangeRedux
) => (weekday: number) => {
  if (!isDefined(hvacTimeSlot.timetable.rows)) {
    return;
  }
  let editedOccupiedSlots = occupiedSlots;

  const newWeekdays = hvacTimeSlot.timetable.rows[0].weekdays.includes(
    WEEKDAYS[weekday]
  )
    ? hvacTimeSlot.timetable.rows[0].weekdays.filter(
        d => d !== WEEKDAYS[weekday]
      )
    : [...hvacTimeSlot.timetable.rows[0].weekdays, WEEKDAYS[weekday]];

  editedOccupiedSlots[index] = {
    ...hvacTimeSlot,
    timetable: {
      ...hvacTimeSlot.timetable,
      rows: [{ ...hvacTimeSlot.timetable.rows[0], weekdays: newWeekdays }],
    },
  };

  let newTimeSlots = [...editedOccupiedSlots, unoccupiedSlot];

  onFormChangeRedux('components.0.hvacSettings.hvacSchedules', newTimeSlots);
};

export const useHvacSopDataToEdit = (): EditSop | undefined => {
  const idBeingEdited = useSelector<ApplicationState, string | number>(
    ({ sops }) => {
      return sops.idBeingEdited;
    }
  );

  const hvacSopToEdit = useSelector<ApplicationState, EditSop>(({ sops }) => {
    return sops.editById[idBeingEdited];
  });

  return isDefined(hvacSopToEdit) ? hvacSopToEdit : undefined;
};

const useSetHvacSop = () => {
  const dispatch = useDispatch();
  const idBeingEdited = useSelector<ApplicationState, string | number>(
    ({ sops }) => {
      return sops.idBeingEdited;
    }
  );

  return (field: string, value: any) =>
    dispatch(updateField(String(idBeingEdited), field, value));
};

export default EditHvacSopForm;
